import Accordion from "@/components/Ui/Accordion";
import Icon from "@/components/Ui/Icon";
import userHasScopes from "@/components/VisibleForScopes/userHasScopes";
import { Badge } from "@/components/shadcn/badge";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from "@/components/shadcn/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { DELIVERY_PRODUCTS } from "@/constants/deliveries";
import { useSesamyAuthTokenContext } from "@/contexts/sesamy-auth-token-context";
import { useVendorContext } from "@/contexts/vendor-context";
import useBadges from "@/hooks/use-badges";
import useUniversalRouter from "@/hooks/use-universal-router";
import navItems, { getActivePageIds } from "@/utils/navigation";
import { preserveQueryString } from "@/utils/query-string";
import { IconName } from "icons/types";
import { ChevronDown, Package } from "lucide-react";
import Link from "next/link";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const SideNav = () => {
  const { multiVendorKey, selectedVendor } = useVendorContext();
  const { setOpen, open: sidebarOpen } = useSidebar();
  const { pathname, asPath } = useUniversalRouter();
  const [activePageId, activeSubPageId] = getActivePageIds(pathname);
  const { scopesWeHaveForVendor } = useSesamyAuthTokenContext();
  const productsVisible = useFeatureFlagEnabled("products-view");
  const integrationsVisible = useFeatureFlagEnabled(
    "settings-integrations-view",
  );
  const { badges } = useBadges();
  const startPageUrl = selectedVendor ? `/${selectedVendor}/analytics` : "/";
  const [openId, setOpenId] = useState<string | null>(null);
  const [activeOpen, setActiveOpen] = useState(true);
  const wasOpenOnEnter = useRef(false);

  useEffect(() => {
    const handler = ({ detail }: any) => setOpen(detail);
    document.addEventListener("menu-expanded", handler);
    return () => document.removeEventListener("menu-expanded", handler);
  }, [setOpen]);

  useEffect(() => {
    if (!sidebarOpen) {
      setOpenId(null);
      setActiveOpen(false);
    } else {
      setActiveOpen(true);
    }
  }, [sidebarOpen]);

  if (multiVendorKey === selectedVendor) return null;

  const filteredNavItems = scopesWeHaveForVendor
    ? navItems
        .map((navItem) => ({
          ...navItem,
          subItems: navItem.subItems.filter(({ id, scopes, featureFlag }) => {
            if (!userHasScopes(scopes, scopesWeHaveForVendor)) return false;
            if (featureFlag === "products-view") return !!productsVisible;
            if (featureFlag === "settings-integrations-view")
              return !!integrationsVisible;
            if (id === "deliveries")
              return !!DELIVERY_PRODUCTS[selectedVendor || ""];
            return true;
          }),
        }))
        .map((navItem) => ({
          ...navItem,
          subItems: navItem.subItems.map((subItem) => {
            if (subItem.id === "manual_payments")
              return {
                ...subItem,
                badge: badges?.manualPayments?.unsent,
                badgeTooltip: "Unsent invoices",
              };
            return subItem;
          }),
        }))
        .filter(
          ({ scopes, subItems }) =>
            userHasScopes(scopes, scopesWeHaveForVendor) && subItems.length > 0,
        )
    : [];

  return (
    <Sidebar
      collapsible="icon"
      className="overflow-hidden"
      onMouseEnter={() => {
        wasOpenOnEnter.current = sidebarOpen;
        setOpen(true);
      }}
    >
      <SidebarHeader>
        <Link href={startPageUrl} className="overflow-hidden px-0.5 pt-4 pb-2">
          <Icon className="text-2xl" name="sesamy-portal" />
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredNavItems.map(({ id, label, icon, subItems }) => {
                const isActive = id === activePageId;
                const hasBadge = subItems.some((s) => !!s.badge);
                const iconEl =
                  icon === "package" ? (
                    <Package style={{ width: "1em", height: "1em" }} />
                  ) : (
                    <Icon name={icon as IconName} />
                  );

                if (subItems.length === 1) {
                  const { basePath, href } = subItems[0];
                  return (
                    <SidebarMenuItem key={id}>
                      <SidebarMenuButton asChild isActive={isActive}>
                        <Link
                          href={`${basePath || ""}/${selectedVendor}${href}${preserveQueryString(asPath)}`}
                        >
                          <div className="relative">
                            {iconEl}
                            {hasBadge && (
                              <span className="bg-destructive absolute -top-1 -right-1 size-2 rounded-full" />
                            )}
                          </div>
                          <span>{label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                }

                const isOpen = isActive ? activeOpen : openId === id;
                const toggle = () =>
                  isActive
                    ? setActiveOpen((v) => !v)
                    : setOpenId(openId === id ? null : id);

                return (
                  <SidebarMenuItem key={id}>
                    <SidebarMenuButton isActive={isActive} onClick={toggle}>
                      <div className="relative">
                        {iconEl}
                        {hasBadge && (
                          <span className="bg-destructive absolute -top-1 -right-1 size-2 rounded-full" />
                        )}
                      </div>
                      <span>{label}</span>
                      <ChevronDown
                        className={twMerge(
                          "ml-auto transition-transform group-data-[collapsible=icon]:hidden",
                          isOpen && "scale-y-[-1]",
                        )}
                      />
                    </SidebarMenuButton>
                    <Accordion isOpen={isOpen}>
                      <SidebarMenuSub className="mr-0 pr-0">
                        {subItems.map(
                          ({
                            id: subId,
                            label: subLabel,
                            href,
                            basePath,
                            badge,
                            badgeTooltip,
                          }) => (
                            <SidebarMenuSubItem key={subId}>
                              <SidebarMenuSubButton
                                asChild
                                isActive={subId === activeSubPageId}
                                className="w-full"
                              >
                                <Link
                                  href={`${basePath || ""}/${selectedVendor}${href}${preserveQueryString(asPath)}`}
                                >
                                  {subLabel}
                                  {!!badge && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Badge
                                          variant="destructive"
                                          className="ml-auto h-5 min-w-5 px-1 text-[10px]"
                                        >
                                          {badge}
                                        </Badge>
                                      </TooltipTrigger>
                                      {badgeTooltip && (
                                        <TooltipContent>
                                          {badgeTooltip}
                                        </TooltipContent>
                                      )}
                                    </Tooltip>
                                  )}
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ),
                        )}
                      </SidebarMenuSub>
                    </Accordion>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter />
      <SidebarRail />
    </Sidebar>
  );
};

export default SideNav;
